import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [pendingMsg, setPendingMsg] = useState("");

  const [lEmail, setLEmail] = useState("");
  const [lPass, setLPass] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotId, setForgotId] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      const res = await api.post("/auth/forgot-password", { identifier: forgotId.trim() });
      setForgotMsg(res.data.message || "Solicitação enviada.");
    } catch { setForgotMsg("Solicitação enviada."); }
    finally { setForgotBusy(false); }
  };

  const [r, setR] = useState({
    name: "", email: "", password: "", role: "membro",
    phone: "", address: "", birth_date: "",
  });
  const [roleOpts, setRoleOpts] = useState([]);
  const setField = (k, v) => setR((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get("/roles", { params: { public_only: true } }).then((res) => setRoleOpts(res.data)).catch(() => {});
  }, []);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(lEmail.trim(), lPass);
    setLoading(false);
    if (res.ok) {
      toast.success("Bem-vindo de volta!");
      navigate("/dashboard");
    } else toast.error(res.error);
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const invite = params.get("invite") || "";
    const res = await register({ ...r, name: r.name.trim(), email: r.email.trim(), invite_token: invite });
    setLoading(false);
    if (res.ok && res.pending) {
      setPendingMsg(res.message);
      toast.success("Cadastro enviado!");
    } else if (res.ok) {
      toast.success("Conta criada!");
      navigate("/dashboard");
    } else toast.error(res.error);
  };

  const invite = params.get("invite");

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden bg-[#0B0F17]">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: "url(https://customer-assets-m6fa6gv7.emergentagent.net/job_webapp-design-2/artifacts/bb6gdc5b_WhatsApp%20Image%202026-08-12%20at%2014.57.02.jpeg)",
          backgroundSize: "cover", backgroundPosition: "center",
        }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/70 to-transparent" />
        <div className="relative z-10"><Logo /></div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-display text-4xl font-bold text-white leading-tight">
            Pequenos Grupos de <span className="text-accent">Discipulado</span>
          </h1>
          <p className="mt-4 text-slate-300 leading-relaxed">
            Organização, acompanhamento e resultados sustentáveis para a jornada de discipulado da Zona Norte.
          </p>
        </div>
        <div className="relative z-10 text-xs text-slate-500">© Aprisco Church — Zona Norte</div>
      </div>

      <div className="flex flex-col justify-center px-6 sm:px-12 py-12 overflow-y-auto">
        <div className="w-full max-w-md mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="h-4 w-4" /> Voltar ao início
          </Link>
          <div className="lg:hidden mb-8"><Logo /></div>
          <h2 className="font-display text-3xl font-bold mb-1">Área de Gestão</h2>
          <p className="text-muted-foreground mb-8">Acesse ou cadastre-se na plataforma dos PGDs.</p>

          <Tabs defaultValue={params.get("tab") === "register" || params.get("invite") ? "register" : "login"}>
            <TabsList className="grid grid-cols-2 w-full mb-6">
              <TabsTrigger value="login" data-testid="tab-login">Entrar</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Cadastre-se</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={doLogin} className="space-y-4">
                <div>
                  <Label htmlFor="le">E-mail</Label>
                  <Input id="le" type="email" value={lEmail} onChange={(e) => setLEmail(e.target.value)}
                    data-testid="login-email-input" required className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="lp">Senha</Label>
                  <Input id="lp" type="password" value={lPass} onChange={(e) => setLPass(e.target.value)}
                    data-testid="login-password-input" required className="mt-1.5" />
                  <button type="button" onClick={() => { setForgotOpen(true); setForgotMsg(""); setForgotId(lEmail); }}
                    className="mt-2 text-sm text-primary hover:underline" data-testid="forgot-password-link">
                    Esqueceu a senha?
                  </button>
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                  {loading ? "Entrando…" : "Entrar"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              {pendingMsg ? (
                <div className="rounded-xl border border-accent/30 bg-accent/5 p-6 text-center" data-testid="register-pending-message">
                  <CheckCircle2 className="h-12 w-12 text-accent mx-auto mb-3" />
                  <h3 className="font-display text-lg font-semibold">Cadastro enviado!</h3>
                  <p className="text-sm text-muted-foreground mt-2">{pendingMsg}</p>
                </div>
              ) : (
                <form onSubmit={doRegister} className="space-y-4">
                  {invite && (
                    <div className="rounded-lg border border-accent/30 bg-accent/5 px-4 py-3 text-sm text-accent" data-testid="invite-banner">
                      ✨ Você foi convidado(a) como <strong>Líder</strong>. Seu acesso será liberado automaticamente ao concluir o cadastro.
                    </div>
                  )}
                  {!invite && (
                  <div>
                    <Label>Tipo de cadastro</Label>
                    <Select value={r.role} onValueChange={(v) => setField("role", v)}>
                      <SelectTrigger className="mt-1.5" data-testid="register-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {roleOpts.map((o) => (
                          <SelectItem key={o.key} value={o.key}>
                            {o.label}{o.requires_approval ? " (requer aprovação)" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  )}
                  <div>
                    <Label>Nome completo</Label>
                    <Input value={r.name} onChange={(e) => setField("name", e.target.value)} required className="mt-1.5" data-testid="register-name-input" />
                  </div>
                  <div>
                    <Label>E-mail</Label>
                    <Input type="email" value={r.email} onChange={(e) => setField("email", e.target.value)} required className="mt-1.5" data-testid="register-email-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Telefone</Label>
                      <Input value={r.phone} onChange={(e) => setField("phone", e.target.value)} required className="mt-1.5" data-testid="register-phone-input" placeholder="(11) 90000-0000" />
                    </div>
                    <div>
                      <Label>Data de nascimento</Label>
                      <Input type="date" value={r.birth_date} onChange={(e) => setField("birth_date", e.target.value)} required className="mt-1.5" data-testid="register-birthdate-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Endereço</Label>
                    <Input value={r.address} onChange={(e) => setField("address", e.target.value)} required className="mt-1.5" data-testid="register-address-input" />
                  </div>
                  <div>
                    <Label>Senha</Label>
                    <Input type="password" value={r.password} onChange={(e) => setField("password", e.target.value)} required minLength={6} className="mt-1.5" data-testid="register-password-input" />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-button">
                    {loading ? "Enviando…" : "Cadastrar"}
                  </Button>
                </form>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle>Esqueceu a senha?</DialogTitle>
            <DialogDescription>Informe seu e-mail ou telefone cadastrado. Os líderes/coordenadores serão avisados para redefinir sua senha e entrarão em contato com você.</DialogDescription>
          </DialogHeader>
          {forgotMsg ? (
            <div className="rounded-xl border border-accent/30 bg-accent/5 p-5 text-center" data-testid="forgot-success-message">
              <CheckCircle2 className="h-10 w-10 text-accent mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{forgotMsg}</p>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="space-y-4">
              <div>
                <Label htmlFor="fid">E-mail ou telefone</Label>
                <Input id="fid" value={forgotId} onChange={(e) => setForgotId(e.target.value)} required className="mt-1.5"
                  placeholder="seu@email.com ou (11) 90000-0000" data-testid="forgot-identifier-input" />
              </div>
              <DialogFooter>
                <Button type="submit" className="w-full" disabled={forgotBusy} data-testid="forgot-submit-button">
                  {forgotBusy ? "Enviando…" : "Solicitar redefinição"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
